import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import logger from 'src/utils/logger';
import ContainerBody from '../components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { OverlayActions, useOverlay } from '../providers/OverlayProvider';
import Button, { ButtonToneProps } from '../components/ui/Button/Button';
import { UpdateContactFormValues, ContactOverlayForm } from '../components/layouts/ContactOverlay/ContactOverlayForm';
import { useContactArchive, useContactUpdate, useSingleContactQuery } from '../services/contact';
import LoadingSkeleton from '../components/ui/LoadingSkeleton/LoadingSkeleton';
import Divider from '../components/ui/Divider/Divider';

export function ContactOverlay() {
  const { id } = useParams();
  const { onClose } = useOverlay();
  const numericId = Number(id);

  if (!id && Number.isNaN(numericId)) {
    throw new Error(`Invalid contact ID: ${id}`);
  }

  const { data: partner, isFetching } = useSingleContactQuery(numericId);
  const { t } = useTranslation();
  const [editMode, setEditMode] = React.useState(false);

  React.useEffect(() => {
    setEditMode(false);
  }, [id]);

  const mutation = useContactUpdate();

  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = (data: UpdateContactFormValues) => {
    mutation.mutate(
      {
        params: {
          path: {
            partner_id: numericId,
          },
        },
        body: data,
      },

      {
        onSuccess: () => {
          setEditMode(false);
        },
      },
    );
  };

  const archiveMutation = useContactArchive();

  const triggerSubmit = () => {
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const archive = async () => {
    logger.info('Archiving contact', numericId);
    try {
      await archiveMutation.mutateAsync({
        params: {
          path: {
            partner_id: numericId,
          },
        },
      });
      toast.success(t('contact.success.archived'));
      onClose();
    } catch (e) {
      logger.error('Failed to archive contact', e);
      toast.error(t('contact.error.archive'));
    }
  };

  return (
    <>
      {editMode && (
        <OverlayActions>
          <Button title={t('general.cancel')} onClick={() => setEditMode(false)} tone={ButtonToneProps.SECONDARY} />
          <Button title={t('general.save')} tone={ButtonToneProps.PRIMARY_BLUE} onClick={triggerSubmit} />
        </OverlayActions>
      )}
      {!editMode && (
        <OverlayActions>
          <Button title={t('general.edit')} onClick={() => setEditMode(true)} tone={ButtonToneProps.PRIMARY_BLUE} />
        </OverlayActions>
      )}
      <ContainerBody className="h-full">
        <div className="flex md:flex-row gap-16 md:items-center items-start mb-12 justify-between flex-col">
          <h2 className="text-full typo-title-36">{partner?.name}</h2>
          {editMode && (
            <Button title={t('contact.archive_contact')} onClick={() => archive()} tone={ButtonToneProps.DESTRUCTIVE} />
          )}
        </div>
        <Divider className="my-24" />
        <div className="text-full typo-text-16--bold mb-20">{t('general.contact_details')}</div>
        {!isFetching && partner && (
          <div className="flex flex-col gap-35">
            <ContactOverlayForm
              ref={formRef}
              mode="update"
              onSubmit={onSubmit}
              readOnly={!editMode}
              partner={partner}
            />
          </div>
        )}
        {isFetching && <LoadingSkeleton className="h-full" />}
      </ContainerBody>
    </>
  );
}
